import { useEffect, useRef, useState } from 'react';
import type { CopilotSessionStatus, CopilotStatefulMessage } from '@customerio/hydra-copilot';

const STALL_THRESHOLD_MS = 200;

/**
 * Returns true when the session is in `receiving` status but no new messages
 * have arrived for at least `STALL_THRESHOLD_MS` milliseconds.
 *
 * This lets us show a "Working…" indicator when the model is still streaming
 * but hasn't sent any new content recently (e.g. thinking, executing tools).
 */
export function useCopilotStalled(status: CopilotSessionStatus | null, messages: CopilotStatefulMessage[]): boolean {
  const [isStalled, setIsStalled] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only track stalls while receiving
    if (status !== 'receiving') {
      setIsStalled(false);
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Every time messages change while receiving, reset the timer
    setIsStalled(false);
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setIsStalled(true);
    }, STALL_THRESHOLD_MS);

    return () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status, messages]);

  return isStalled;
}
